import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CommonButton from './CommonButton';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onGoHome?: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error caught by ErrorBoundary:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleGoHome = (): void => {
    this.setState({ error: null });
    this.props.onGoHome?.();
  };

  render(): React.ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          display: 'grid',
          placeItems: 'center',
          p: { xs: 2, md: 4 },
          background: 'linear-gradient(180deg, #f8fcfd 0%, #eef6f8 100%)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 860,
            borderRadius: 4,
            border: '1px solid #e2eef1',
            backgroundColor: '#ffffff',
            boxShadow: '0 18px 40px rgba(76, 155, 171, 0.18)',
            px: { xs: 3, md: 6 },
            py: { xs: 5, md: 6 },
          }}
        >
          <Stack spacing={1.5} alignItems="center" textAlign="center">
            <ReportProblemRoundedIcon sx={{ fontSize: 52, color: '#2f7d8c' }} />
            <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, color: '#2f7d8c' }}>
              Something Went Wrong
            </Typography>
            <Typography sx={{ maxWidth: 520, color: '#5f7480' }}>
              An unexpected error occurred while rendering this page. Try reloading — if the problem
              continues, contact your administrator.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
              <CommonButton
                label="Reload Page"
                startIcon={<RefreshRoundedIcon />}
                variant="outlined"
                onClick={this.handleReload}
              />
              {this.props.onGoHome ? (
                <CommonButton label="Back To Home" startIcon={<HomeRoundedIcon />} onClick={this.handleGoHome} />
              ) : null}
            </Stack>
          </Stack>
        </Box>
      </Box>
    );
  }
}

export default ErrorBoundary;
